import { getAuthToken } from './auth/tokenStorage';
import AsyncStorage from '@react-native-async-storage/async-storage';

import { API_URL } from '../config';

export interface MedicalHistoryRec {
  id: string;
  taskId: number;
  diagnosisDate: string;
  condition: string;
  treatingPhysician: string;
  treatmentDescription: string;
  medications: string;
  recoveryNotes: string;
  followupRequired: string;
  additionalData?: Record<string, any> | null;
}

export interface MedicalHistoryInfo extends MedicalHistoryRec {}

export interface CreateMedicalHistoryData {
  taskId: number;
  diagnosisDate?: string;
  condition?: string;
  treatingPhysician?: string;
  treatmentDescription?: string;
  medications?: string;
  recoveryNotes?: string;
  followupRequired?: string;
  additionalData?: Record<string, any> | null;
}

export interface UpdateMedicalHistoryData extends CreateMedicalHistoryData {}

class MedicalHistoryInfoService {
  private async getAuthToken(): Promise<string | null> {
    return await getAuthToken();
  }

  private async makeRequest(endpoint: string, options: RequestInit = {}): Promise<Response> {
    const token = await this.getAuthToken();
    
    const response = await fetch(`${API_URL}${endpoint}`, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        ...(token && { 'Authorization': `Bearer ${token}` }),
        ...options.headers,
      },
    });

    return response;
  }

  async getAllMedicalHistoryInfo(targetUserId?: string): Promise<MedicalHistoryRec[]> {
    const url = targetUserId
      ? `/api/medical-history-info?targetUserId=${targetUserId}`
      : '/api/medical-history-info';
    const response = await this.makeRequest(url);

    if (!response.ok) {
      throw new Error(`Failed to fetch medical history info: ${response.status}`);
    }

    return await response.json();
  }

  async getMedicalHistoryById(id: string): Promise<MedicalHistoryRec> {
    const response = await this.makeRequest(`/api/medical-history-info/${id}`);

    if (!response.ok) {
      throw new Error(`Failed to fetch medical history info: ${response.status}`);
    }

    return await response.json();
  }

  async getMedicalHistoryInfo(taskId: number): Promise<MedicalHistoryInfo[]> {
    const response = await this.makeRequest(`/api/medical-history-info?taskId=${taskId}`);

    if (!response.ok) {
      throw new Error(`Failed to fetch medical history info: ${response.status}`);
    }

    return await response.json();
  }

  async createMedicalHistoryInfo(data: CreateMedicalHistoryData): Promise<{ id: string }> {
    const response = await this.makeRequest('/api/medical-history-info', {
      method: 'POST',
      body: JSON.stringify(data),
    });
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `Failed to create medical history info: ${response.status}`);
    }
    
    return await response.json();
  }

  async updateMedicalHistoryInfo(id: string, data: UpdateMedicalHistoryData): Promise<MedicalHistoryInfo> {
    const response = await this.makeRequest(`/api/medical-history-info/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `Failed to update medical history info: ${response.status}`);
    }
    
    return await response.json();
  }

  async deleteMedicalHistoryInfo(id: string): Promise<void> {
    const response = await this.makeRequest(`/api/medical-history-info/${id}`, { method: 'DELETE' });

    if (!response.ok) {
      throw new Error(`Failed to delete medical history info: ${response.status}`);
    }
  }

  async setVisibilityState(id: string, visibilityState: 'LOCKED' | 'UNLOCKED'): Promise<MedicalHistoryInfo> {
    const response = await this.makeRequest(`/api/medical-history-info/${id}`, {
      method: 'PATCH',
      body: JSON.stringify({ visibilityState }),
    });
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `Failed to update visibility state: ${response.status}`);
    }
    
    return await response.json();
  }

  async markAsCompleted(taskId: number): Promise<boolean> {
    const response = await this.makeRequest(`/api/medical-history-info/complete/${taskId}`, {
      method: 'POST',
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `Failed to mark medical history info as completed: ${response.status}`);
    }

    return await response.json();
  }

  async lockMedicalHistoryInfo(id: string): Promise<void> {
    const response = await this.makeRequest(`/api/medical-history-info/${id}/lock`, {
      method: 'POST',
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `Failed to lock medical history info: ${response.status}`);
    }
  }

  async unlockMedicalHistoryInfo(id: string, granteeEmails: string[]): Promise<void> {
    const response = await this.makeRequest(`/api/medical-history-info/${id}/unlock`, {
      method: 'POST',
      body: JSON.stringify({ granteeEmails }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `Failed to unlock medical history info: ${response.status}`);
    }
  }

  async getMedicalHistoryAccessGrants(id: string): Promise<any[]> {
    const response = await this.makeRequest(`/api/medical-history-info/${id}/access-grants`);

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `Failed to get medical history access grants: ${response.status}`);
    }

    const result = await response.json();
    return result.data || [];
  }
}

export const medicalHistoryInfoService = new MedicalHistoryInfoService();
export const medicalHistoryService = medicalHistoryInfoService;


























